import { DatePipe, registerLocaleData } from '@angular/common';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import {
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  ErrorHandler,
  APP_INITIALIZER,
} from '@angular/core';
import { RouterModule, RouteReuseStrategy } from '@angular/router';
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import localeJa from '@angular/common/locales/ja';
import localeZh from '@angular/common/locales/zh';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeNb from '@angular/common/locales/nb';
import localeRu from '@angular/common/locales/ru';
import localeAr from '@angular/common/locales/ar';
import localeCs from '@angular/common/locales/cs';
import localePl from '@angular/common/locales/pl';
import localeFi from '@angular/common/locales/fi';
import localeDa from '@angular/common/locales/da';
import localeSv from '@angular/common/locales/sv';

import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import {
  TRANSLATION_PROVIDER,
  CoreModule,
  AppConfigService,
  DebugAppConfigService,
  IdentityUserInfoComponent,
  BlankPageComponent,
  CONTEXT_MENU_DIRECTIVES,
  AuthModule,
  BasicAlfrescoAuthService,
  ErrorWidgetComponent,
  AuthenticationService,
} from '@alfresco/adf-core';
import { ProcessModule } from '@alfresco/adf-process-services';
import {
  ContentModule,
  ContentNodeDialogService,
  CONTENT_UPLOAD_DIRECTIVES,
  ContentVersionService,
  TreeViewService,
} from '@alfresco/adf-content-services';
import {
  MAT_RIPPLE_GLOBAL_OPTIONS,
  RippleGlobalOptions,
} from '@angular/material/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatTreeModule } from '@angular/material/tree';
import {
  AppService,
  ContentApiService,
  DocumentBasePageService,
  SharedModule,
  SharedToolbarModule,
  ToolbarComponent,
} from '@alfresco/aca-shared';
import {
  EXTENSION_DIRECTIVES,
  provideExtensionConfig,
} from '@alfresco/adf-extensions';
import { environment } from '@adw/src/environments/environment';
import { AosEditOnlineService } from '@adw/projects/aca-content/ms-office/src/aos-extension.service';

import { APP_ROUTES } from './app.routes';
import { CustomFilesComponent } from './components/custom-files/custom-files.component';
import { CKEditorModule } from './components/ckeditor';
import { SaveVersionsComponent } from './dialogs/save-versions/save-versions.dialog';
import { SaveChangesComponent } from './dialogs/save-changes/save-changes.dialog';
import { NodeProcessDialogComponent } from './dialogs/node-process/node-process.dialog';
import { WkContentNodeSelectorComponent } from './components/wk-content-node-selector/wk-content-node-selector.component';
import { AppRouteReuseStrategy } from './app.routes.strategy';
import {
  WkContentApiService,
  LocaleService,
  NodeInfoService,
  WkContentNodeDialogService,
  WKErrorHandler,
  WkAuthenticationService,
} from './services';
import { RAW_AOS_SERVICE } from './services/tokens';
import { WkAosExtensionService } from './services/wk-aos-extension.service';
import { WkToggleEditOfflineComponent } from './components/wk-toggle-edit-offline/wk-toggle-edit-offline.component';
import { WkSiteContainersComponent } from './components/wk-site-containers/wk-site-containers.component';
import { WkLogoutModule } from './components/wk-logout/wk-logout.module';
import { MaterialModule } from './material.module';
import { AppStoreModule } from './store/app-store.module';
import { WkLoginModule } from './components/wk-login/wk-login.module';
import { CustomDirectivesModule } from './components/shared/directives/custom-directives.module';
import { WkShareDocumentModule } from './components/wk-share-document/wk-share-document.module';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { WkResetPasswordComponent } from './components/wk-reset-password/wk-reset-password.component';
import { WKDeleteCitationsComponent } from './dialogs/wk-delete-citations/wk-delete-citations';
import { WkUploadWidgetComponent } from './widgets/wk-upload/wk-upload.widget';
import { CitationCountPipe } from './dialogs/wk-delete-citations/wk-delete-citations.pipe';
import { CompareAndMergeDialogComponent } from './dialogs/compare-and-merge/compare-and-merge.dialog';
import { WkCompareVersionsComponent } from './components/wk-compare-versions/wk-compare-versions.component';
import { WkTemplateDialogComponent } from './dialogs/wk-template/wk-template.dialog';
import { WkTemplateNamePipe } from './dialogs/wk-template/wk-template.pipe';
import { WkFormsModule } from './components/wk-forms/wk-forms.module';
import { WkContentNodeSelectorWidgetComponent } from './components/wk-content-node-selector-widget/wk-content-node-selector-widget.component';
import { WkCompareDocumentsComponent } from './components/wk-compare-documents/wk-compare-documents.component';
import { UploadVersionDialogComponent } from './dialogs/upload-version/upload-version.dialog';
import { WkPeopleWidgetComponent } from './widgets/wk-people/wk-people.widget';
import { BlobUrlPipe } from './widgets/wk-people/wk-blob-url.pipe';
import { CitationDeleteMultipleRefsPipe } from './dialogs/wk-delete-citations/wk-delete-multiple-refs.pipe';
import { WKInsertMediaComponent } from './dialogs/wk-insert-media/wk-insert-media.component';
import { WkUploadImageComponent } from './components/editor/wk-media/wk-upload-image/wk-upload-image.component';
import { WKMediaComponent } from './components/editor/wk-media/wk-media.component';
import { WkMediaTreeViewComponent } from './components/editor/wk-media/wk-custom-tree-view/wk-custom-tree-view.component';
import { WkFileUploadComponent } from './dialogs/wk-file-upload/wk-file-upload.component';
import { WkUploadFilesListComponent } from './components/wk-upload-files-list/wk-upload-files-list.component';
import { WkUploadFilesDropdownComponent } from './components/wk-upload-files-dropdown/wk-upload-files-dropdown.component';
import { NotificationDialogComponent } from './dialogs/notification/notification.dialog';
import { WkConfirmDialogComponent } from './dialogs/wk-confirm-dialog/wk-confirm-dialog';
import { WkUpdatePropertyDialogComponent } from './dialogs/wk-update-property/wk-update-property.dialog';
import { ClipNamePipe } from './dialogs/notification/clip-name.pipe';
import { WkCommentsTabModule } from './components/info-drawer/wk-comments-tab/wk-comments-tab.module';
import { WKInsertLinkComponent } from './dialogs/wk-insert-link/wk-insert-link.component';
import { WKWebEmailAddressLinkComponent } from './dialogs/wk-web-email-address-link/wk-web-email-address-link.component';
import { WkLinkDocumentComponent } from './dialogs/wk-link-document/wk-link-document.component';
import { WINDOW_TOKEN } from './components/shared/window.token';
import { WkBreadCrumbModule } from './components/wk-breadcrumb/wk-breadcrumb.module';
import { WkAppComponent } from './forked/components/wk-app-component/wk-app.component';
import { WkAppLayoutModule } from './forked/components/layout/layout.module';
import { WkCleanUpCommentsComponent } from './dialogs/clean-up-comments/wk-clean-up-comments.dialog';
import { WkImagePreviewDialogComponent } from './dialogs/wk-image-preview/wk-image-preview.dialog';
import { WkSearchResultsModule } from './components/wk-search-results/wk-search-results.module';
import { WkSemanticSearchResultsModule } from './components/wk-semantic-search-results/wk-semantic-search-results.module';
import { WkDocumentListModule } from './forked/components/wk-document-list/wk-document-list.module';
import { WkUnassignableDocumentDialogComponent } from './dialogs/wk-unassignable-document-dialog/wk-unassignable-document-dialog.component';
import { WkCardViewDateItemComponent } from './components/wk-card-view-date-item/wk-card-view-date-item.component';
import { WkDateCellComponent } from './components/wk-date-cell/wk-date-cell.component';
import { WkDateWidgetComponent } from './components/wk-date-widget/wk-date-widget.component';
import { WkKeywordModule } from './components/keyword';
import { SentenceCasePipe } from './pipes/sentencecase.pipe';
import { ConfigurationModule } from './extensions/configurations.module';
import { NodeVersionsDialogComponent } from './components/wk-node-versions/wk-node-versions.dialog';
import { CoreExtensionsModule } from './extensions/core.extensions.module';
import { AppInfoDrawerModule } from '@adw/projects/aca-content/src/lib/components/info-drawer/info.drawer.module';
import { WkStartProcessInstanceComponent } from './components/wk-start-process/wk-start-process.component';
import { WkInfodrawerService } from './components/info-drawer/services/wk-info-drawer.service';
import { ContentManagementService } from '@adw/projects/aca-content/src/lib/services/content-management.service';
import {
  SHELL_APP_SERVICE,
  SHELL_AUTH_TOKEN,
  ShellModule,
} from '@alfresco/adf-core/shell';
import { WkAppShellService } from './services/wk-app-shell.service';
import { WkPermissionsModule } from './components/permissions/wk-permissions.module';
import { WKDocumentDecoratorModule } from './modules/wk-document-decorator';
import { WkContentNodeSelectorPanelComponent } from './components/wk-content-node-selector-panel/wk-content-node-selector-panel.component';
import { AppExtensionsModule } from '@adw/src/app/extensions.module';
import { TranslateModule } from '@ngx-translate/core';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  CONTENT_LAYOUT_ROUTES,
  ContentServiceExtensionModule,
  ContentUrlService,
} from '@adw/projects/aca-content/src/public-api';
import { WkBasicAlfrescoAuthService } from './services/wk-basic-alfresco-auth.service';
import { WkAuthGuard } from './guards/wk-auth.guard';
import { MsalModule } from './msal/msal.module';
import { FileUploadInterceptor } from './interceptors/file-upload.interceptor';
import { CsrfTokenInterceptor } from './interceptors/csrf-token.interceptor';
import { WkTreeViewService } from './services/wk-tree-view.service';
import { SafeResource } from './components/shared/pipes/safe-resource/safe-resource';
import { WkReassignTaskDialogComponent } from './dialogs/wk-reassign-task/wk-reassign-task.dialog';
import { WkConfirmCancelCheckoutComponent } from './dialogs/wk-confirm-cancel-checkout/wk-confirm-cancel-checkout.component';

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

registerLocaleData(localeEn); // English (default fallback)
registerLocaleData(localeFr); // French
registerLocaleData(localeDe); // German
registerLocaleData(localeIt); // Italian
registerLocaleData(localeEs); // Spanish
registerLocaleData(localeJa); // Japanese
registerLocaleData(localeZh); // Chinese
registerLocaleData(localeNl); // Dutch
registerLocaleData(localePt); // Portuguese
registerLocaleData(localeNb); // Norwegian
registerLocaleData(localeRu); // Russian
registerLocaleData(localeAr); // Arabic
registerLocaleData(localeCs); // Czech
registerLocaleData(localePl); // Polish
registerLocaleData(localeFi); // Finnish
registerLocaleData(localeDa); // Danish
registerLocaleData(localeSv); // Swedish

@NgModule({
  imports: [
    AuthModule.forRoot({ useHash: true }),
    BrowserModule,
    TranslateModule.forRoot(),
    CoreModule.forRoot(),
    CoreExtensionsModule.forRoot(),
    environment.e2e ? NoopAnimationsModule : BrowserAnimationsModule,
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({ maxAge: 25 }),
    ...EXTENSION_DIRECTIVES,
    ContentModule.forRoot(),
    RouterModule.forRoot(APP_ROUTES, {
      useHash: true,
      enableTracing: false, // enable for debug only
      onSameUrlNavigation: 'reload',
    }),
    ShellModule.withRoutes({
      shellChildren: [CONTENT_LAYOUT_ROUTES],
    }),
    ContentServiceExtensionModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    SharedModule,
    AppStoreModule,
    AppExtensionsModule,
    ConfigurationModule,
    WkLoginModule,
    WkAppLayoutModule,
    ...CONTEXT_MENU_DIRECTIVES,
    AppInfoDrawerModule,
    WkSearchResultsModule,
    HammerModule,
    CKEditorModule,
    ProcessModule,
    CustomDirectivesModule,
    WkLogoutModule,
    WkShareDocumentModule,
    HttpClientModule,
    WkFormsModule,
    MatTreeModule,
    WkCommentsTabModule,
    WkBreadCrumbModule,
    WkDocumentListModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    WkKeywordModule,
    IdentityUserInfoComponent,
    BlankPageComponent,
    ...CONTENT_UPLOAD_DIRECTIVES,
    WkPermissionsModule,
    WKDocumentDecoratorModule,
    SharedToolbarModule,
    MsalModule,
    ToolbarComponent,
    ErrorWidgetComponent,
    MatFormFieldModule,
    MsalModule,
    SafeResource,
    WkSemanticSearchResultsModule,
  ],
  declarations: [
    WkAppComponent,
    CustomFilesComponent,
    WkCompareVersionsComponent,
    WkCompareDocumentsComponent,
    NodeVersionsDialogComponent,
    SaveVersionsComponent,
    SaveChangesComponent,
    NodeProcessDialogComponent,
    CompareAndMergeDialogComponent,
    WkStartProcessInstanceComponent,
    WkContentNodeSelectorComponent,
    WkContentNodeSelectorWidgetComponent,
    WkToggleEditOfflineComponent,
    WkSiteContainersComponent,
    ChangePasswordComponent,
    WkResetPasswordComponent,
    WKDeleteCitationsComponent,
    WkUploadWidgetComponent,
    WkPeopleWidgetComponent,
    CitationCountPipe,
    BlobUrlPipe,
    WkTemplateDialogComponent,
    WkTemplateNamePipe,
    UploadVersionDialogComponent,
    CitationDeleteMultipleRefsPipe,
    WKInsertMediaComponent,
    WKInsertLinkComponent,
    WKWebEmailAddressLinkComponent,
    WkLinkDocumentComponent,
    WKMediaComponent,
    WkMediaTreeViewComponent,
    WkFileUploadComponent,
    WkUploadFilesListComponent,
    WkUploadFilesDropdownComponent,
    NotificationDialogComponent,
    WkConfirmDialogComponent,
    WkUpdatePropertyDialogComponent,
    ClipNamePipe,
    WkCleanUpCommentsComponent,
    WkImagePreviewDialogComponent,
    WkUnassignableDocumentDialogComponent,
    WkCardViewDateItemComponent,
    WkDateCellComponent,
    SentenceCasePipe,
    WkContentNodeSelectorPanelComponent,
    WkUploadImageComponent,
    WkDateWidgetComponent,
    WkReassignTaskDialogComponent,
    WkConfirmCancelCheckoutComponent,
  ],
  providers: [
    WkInfodrawerService,
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    { provide: ContentVersionService, useClass: ContentUrlService },
    {
      provide: SHELL_AUTH_TOKEN,
      useValue: WkAuthGuard,
    },
    { provide: AppConfigService, useClass: DebugAppConfigService },
    { provide: ContentApiService, useClass: WkContentApiService },
    { provide: ContentNodeDialogService, useClass: WkContentNodeDialogService },
    { provide: DocumentBasePageService, useExisting: ContentManagementService },
    { provide: TreeViewService, useClass: WkTreeViewService },
    provideExtensionConfig([
      'app.extensions.json',
      'aos.plugin.json',
      'app.checkout-plugin.json',
      'batch-metadata-update.json',
      'upload-content.json',
      'app.process-plugin.json',
      'app.ces.json',
      'app.viewer.more-plugin.json',
      'app.edit-document-plugin.json',
      'app.microsoft-online-editing.plugin.json',
    ]),
    {
      provide: TRANSLATION_PROVIDER,
      multi: true,
      useValue: {
        name: 'apollo-app',
        source: './assets/translations',
      },
    },
    NodeInfoService,
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: globalRippleConfig,
    },
    { provide: WINDOW_TOKEN, useValue: window },
    /**
     * APP_INITIALIZER ensures locale data is loaded before the application starts.
     */
    {
      provide: APP_INITIALIZER,
      useFactory: (localeService: LocaleService) => () => {
        // setUpLocale() loads the user's preferred locale asynchronously
        // but returns the locale string synchronously for LOCALE_ID
        return localeService.setUpLocale();
      },
      deps: [LocaleService],
      multi: true,
    },
    /**
     * LOCALE_ID tells Angular which locale to use for date/number formatting.
     */
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService) => {
        // Ensure locale is determined before returning
        if (!localeService.currentLocale) {
          localeService.setUpLocale();
        }
        return localeService.currentLocale || localeService.defaultLocale;
      },
      deps: [LocaleService],
      multi: false,
    },
    DatePipe,
    { provide: AppService, useClass: AppService },
    {
      provide: SHELL_APP_SERVICE,
      useClass: WkAppShellService,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'never' },
    },
    { provide: ErrorHandler, useClass: WKErrorHandler },
    {
      provide: BasicAlfrescoAuthService,
      useClass: WkBasicAlfrescoAuthService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FileUploadInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CsrfTokenInterceptor,
      multi: true,
    },
    { provide: RAW_AOS_SERVICE, useClass: AosEditOnlineService },
    { provide: AosEditOnlineService, useExisting: WkAosExtensionService },
    { provide: AuthenticationService, useExisting: WkAuthenticationService },
  ],
  bootstrap: [WkAppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(matIconRegistry: MatIconRegistry) {
    matIconRegistry.setDefaultFontSetClass('material-icons-outlined');
  }
}
